import { useState, useEffect, useCallback, useMemo } from "react";
import "./QuoteForm.css";
import { useNavigate } from "react-router-dom";

import emailjs from "@emailjs/browser";

import { useAuth } from "../../Context/AuthContext";
import { fetchServiceOptions } from "../../Api/Backend/ServiceOption";
import { createSelfPublishingEntry } from "../../Api/Backend/SelfPublishing";

import ProfitCalculator from "./FormSections/ProfitCalculator";
import BookProduction from "./FormSections/BookProduction";
import TermsSection from "./FormSections/TermsSection";
import QuoteTotalsSection from "./FormSections/QuoteTotalsSection";
import GeneralInfoSection from "./FormSections/GeneralInfoSection";
import BookInfoSection from "./FormSections/BookInfoSection";
import ServiceOptionSection, { isPackageRow } from "./FormSections/ServiceOptionSection";
import AdditionalNotesSection from "./FormSections/AdditionalNotesSection";
import PublishingMethodSelector from "./FormSections/PublishingMethodSelector";
import LabelledCheckbox from "../../Components/LabelledCheckbox/LabelledCheckbox";

import { buildCreatePayload, buildQuoteEmailParams } from "./Utils/Utils";

// Which publishing_method_scope values each tab includes.
//   자비        → JABI
//   아마존       → AMAZON
//   자비+아마존  → JABI + AMAZON (combined / union)
const SCOPES_FOR_METHOD = {
  jabi: ["JABI"],
  amazon: ["AMAZON"],
  "jabi-amazon": ["JABI", "AMAZON"],
};

const HARDCOVER_TITLE = "하드커버 출간";

const QuoteForm = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { user, isLoggedIn } = auth;

  const [services, setServices] = useState([]);
  const [publishingMethod, setPublishingMethod] = useState("jabi");
  const [userConsented, setUserConsented] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const [quoteSubtotal, setQuoteSubtotal] = useState(0);
  const [quoteDiscount, setQuoteDiscount] = useState(0);
  const [quoteTax, setQuoteTax] = useState(0);
  const [quoteGrandTotal, setQuoteGrandTotal] = useState(0);

  // Services visible in the active tab (inclusive/union scope).
  const visibleServices = useMemo(() => {
    const allowed = SCOPES_FOR_METHOD[publishingMethod] || [];
    return services.filter((s) => allowed.includes(s.publishingMethodScope));
  }, [services, publishingMethod]);

  const pageCountAffectedServiceTitles = [
    "전문 편집",
    "전문 감수",
    "본문 내지 디자인",
    "번역 서비스",
  ];

  const [publisherInfo, setPublisherInfo] = useState({
    companyName: "에이콘온",
    address: "(07547) 서울시 강서구 양천로 583, A-2009호",
    phone: "02-2653-7600",
    email: "acornpub@acornpub.co.kr",
  });

  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    contact: "",
    email: user?.email || "",
    // email: "",
  });

  const [bookInfo, setBookInfo] = useState({
    title: "",
    author: "",
    completionRate: "",
    numberOfPages: 200,
    manuscriptFiles: null,
    additionalNotes: "",
  });

  const [productionInfoForSubmission, setProductionInfoForSubmission] =
    useState({});
  const [dynamicProductionCost, setDynamicProductionCost] = useState({
    printing: 0,
    paper: 0,
  });

  const handleProductionCostChange = useCallback((cost) => {
    setDynamicProductionCost(cost);
  }, []);

  const handleProductionInfoChange = useCallback((info) => {
    setProductionInfoForSubmission(info);
  }, []);

  const handleBookInfoChange = (e) => {
    const { name, value: rawValue, type, files } = e.target;
    let value = rawValue;

    if (type === "number" || e.target.tagName === "SELECT") {
      value = String(rawValue).replace(/^0+(?=\d)/, "");
      if (value === "") value = "0";
    }

    if (name === "manuscriptFiles" && files && files.length > 0) {
      value = Array.from(files);
    }

    if (name === "completionRate") {
      if (rawValue > 100) value = 100;
      if (rawValue < 0) value = 0;
    }

    setBookInfo((prev) => ({ ...prev, [name]: value }));

    if (name === "numberOfPages") {
      setServices((prev) =>
        prev.map((s) =>
          pageCountAffectedServiceTitles.includes(s.title)
            ? { ...s, qty: value }
            : s
        )
      );
    }
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (id) => {
    setServices((prev) => {
      const target = prev.find((s) => s.serviceOptionId === id);
      if (!target || target.isRequired) return prev;

      const isExclusive = isPackageRow(target.category);

      if (isExclusive && target.isSelected) return prev;

      const willSelect = !target.isSelected;

      return prev.map((s) => {
        if (s.serviceOptionId === id) return { ...s, isSelected: willSelect };
        // Selecting a package deselects sibling packages in the same category.
        if (isExclusive && willSelect && s.category === target.category) {
          return { ...s, isSelected: false };
        }
        return s;
      });
    });
  };

  const handleQuantityChange = (id, newQty) => {
    const value = Math.max(0, Number(newQty));
    setServices(
      services.map((s) => (s.serviceOptionId === id ? { ...s, qty: value } : s))
    );
  };

  // -10% when an Amazon package is paired with a qualifying selection:
  //   amazon tab      → package + 하드커버 출간
  //   jabi-amazon tab → package selected
  const calculateDiscountRate = () => {
    const hasPackage = visibleServices.some(
      (s) => isPackageRow(s.category) && s.isSelected
    );
    const hasHardcover = visibleServices.some(
      (s) => s.title === HARDCOVER_TITLE && s.isSelected
    );

    if (publishingMethod === "jabi-amazon" && hasPackage) return 0.1;
    if (publishingMethod === "amazon" && hasPackage && hasHardcover) return 0.1;
    return 0;
  };

  const recalculateTotals = () => {
    // Only count rows visible in the active tab (negotiable rows have unitPrice 0).
    const serviceSubtotal = visibleServices.reduce((acc, service) => {
      return service.isSelected ? acc + service.qty * service.unitPrice : acc;
    }, 0);

    // Paper-book production cost only applies to tabs that show BookProduction.
    const totalProductionCost =
      publishingMethod === "amazon"
        ? 0
        : dynamicProductionCost.printing + dynamicProductionCost.paper;

    const subtotal = serviceSubtotal + totalProductionCost;
    const discount = Math.round(subtotal * calculateDiscountRate());
    const taxBase = subtotal - discount;
    const tax = Math.round(taxBase * 0.1);

    setQuoteSubtotal(subtotal);
    setQuoteDiscount(discount);
    setQuoteTax(tax);
    setQuoteGrandTotal(taxBase + tax);
  };

  const handleFetchServiceOptions = async () => {
    await fetchServiceOptions({
      onSuccess: (result) => {
        const initialized = result.map((service) => ({
          ...service,
          isSelected: service.isRequired,
          required: service.isRequired,
          qty:
            service.unit === "페이지" &&
              pageCountAffectedServiceTitles.includes(service.title)
              ? bookInfo.numberOfPages
              : 1,
          originalPrice: service.unitPrice,
        }));

        const order = (v) => (v == null ? Number.MAX_SAFE_INTEGER : v);
        const firstByCategory = {};
        initialized.forEach((s) => {
          if (!isPackageRow(s.category)) return;
          const cur = firstByCategory[s.category];
          if (!cur || order(s.displayOrder) < order(cur.displayOrder)) {
            firstByCategory[s.category] = s;
          }
        });
        Object.values(firstByCategory).forEach((s) => {
          s.isSelected = true;
        });

        setServices(initialized);
      },
      onError: (error) => {
        console.error("Error fetching service options:", error);
        alert("서비스 옵션을 불러올 수 없습니다. 다시 로그인해 주세요.");
        navigate("/", { replace: true });
      },
    });
  };

  const handleCreateSelfPublishingEntry = async () => {
    const payload = buildCreatePayload({
      customerInfo: customerInfo,
      bookInfo: bookInfo,
      productionInfo: productionInfoForSubmission,
      services: visibleServices,
      publishingMethod: publishingMethod,
    });
    console.log("Payload: ", payload);

    const files = bookInfo?.manuscriptFiles;

    try {
      const data = await createSelfPublishingEntry({
        ...(isLoggedIn && {auth}), // AUth is only included if user is logged in
        payload: payload,
        files: files,
      });
      console.log("Created successfully", data.manuscriptFiles);

      return data;
    } catch (error) {
      console.error("Error processing the form: ", error);
      if (error.status === 503) {
        alert("이메일 전송 중 오류가 발생했습니다. 다시 시도해 주세요.");
      } else {
        alert(error.message || "양식을 처리하는 중에 문제가 발생했습니다.");
      }

      return null;
    }
  };

  const handleSendEmail = (uploadedFileData) => {
    const templateParams = buildQuoteEmailParams({
      publisherInfo,
      customerInfo,
      bookInfo,
      productionInfo: productionInfoForSubmission,
      services: visibleServices,
      quoteSubtotal,
      quoteTax,
      quoteGrandTotal,
      uploadedFileData,
      publishingMethod,
    });

    return emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE_ID,
      templateParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );
  };

  const handleSubmit = async () => {
    const missingFields = [];
    if (!customerInfo.email?.trim()) missingFields.push("이메일");
    if (!bookInfo.title?.trim()) missingFields.push("도서명");
    if (!bookInfo.author?.trim()) missingFields.push("저자명");
    // 판형 (book format) only applies to tabs with paper-book production.
    if (publishingMethod !== "amazon" && !productionInfoForSubmission.bookFormatId)
      missingFields.push("판형");

    if (missingFields.length > 0) {
      alert(`다음 필수 항목을 작성해 주세요: ${missingFields.join(", ")}`);
      return;
    }

    const confirmed = window.confirm("견적서를 제출하시겠습니까?");
    if (!confirmed) {
      return;
    }

    setIsSending(true);

    try {
      const data = await handleCreateSelfPublishingEntry();
      if (!data) return;

      const uploadedFileData = data?.manuscriptFiles || [];
      await handleSendEmail(uploadedFileData);
      alert("견적 요청이 성공적으로 접수되었습니다. 감사합니다!");
    } catch (error) {
      console.error("Error during submission:", error);
      alert("요청 처리 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (services) {
      recalculateTotals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, dynamicProductionCost, publishingMethod]);

  useEffect(() => {
    handleFetchServiceOptions();
  }, []);

  return (
    <div className="quote-form-layout">
      <div className="quote-section white-bg">
        <GeneralInfoSection
          publisherInfo={publisherInfo}
          customerInfo={customerInfo}
          handleCustomerInfoChange={handleCustomerInfoChange}
        />
      </div>
      <div className="quote-section grey-bg">
        <BookInfoSection
          bookInfo={bookInfo}
          handleBookInfoChange={handleBookInfoChange}
        />
      </div>
      <div className="quote-section white-bg">
        <PublishingMethodSelector
          value={publishingMethod}
          onChange={setPublishingMethod}
        />

        <ServiceOptionSection
          serviceOptions={visibleServices}
          handleCheckboxChange={handleCheckboxChange}
          handleQuantityChange={handleQuantityChange}
          renderExtraComponent={
            publishingMethod === "amazon"
              ? undefined
              : (index, sec) =>
                  // Render the 도서 제작 calculator inside the service wrapper,
                  // after the section that contains a "종이책 제작" service option
                  // (matched by category, independent of the section Header).
                  sec?.subgroups?.some((g) =>
                    g.items.some((it) => it.category === "종이책 제작")
                  ) ? (
                    <BookProduction
                      numberOfPages={bookInfo.numberOfPages}
                      onCostChange={handleProductionCostChange}
                      onProductionInfoChange={handleProductionInfoChange}
                      services={services}
                      setServices={setServices}
                    />
                  ) : null
          }
        />

        <QuoteTotalsSection
          subtotal={quoteSubtotal}
          discount={quoteDiscount}
          tax={quoteTax}
          grandTotal={quoteGrandTotal}
        />
        <ProfitCalculator totalCost={quoteGrandTotal} />
      </div>

      <div className="quote-section white-bg">
        <TermsSection />
      </div>

      <div className="addition-notes-section">
        <AdditionalNotesSection
          name="additionalNotes"
          textValue={bookInfo.additionalNotes}
          handleTextValueChange={handleBookInfoChange}
        />
      </div>

      <div className="quote-section white-bg">
        <LabelledCheckbox
          label={" 개인정보 수집 · 이용에 관한 동의(필수)"}
          checked={userConsented}
          onChange={() => setUserConsented(!userConsented)}
        />

        <div className="print-button-container no-print">
          <button
            className="print-button"
            onClick={() => (isSending ? null : window.print())}
            disabled={isSending}
          >
            인 쇄
          </button>

          <button
            className="print-button"
            onClick={isSending || !userConsented ? null : handleSubmit}
            disabled={isSending || !userConsented}
          >
            <span>보내기</span>
            {isSending && <span className="sending-loader" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
